import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../services/api';
import { Organization, UserOrganizationSummary, Project, PermissionTier, Table, User } from '../types/domain';

interface AuthContextType {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  organizations: UserOrganizationSummary[];
  currentOrg: Organization | null;
  projects: Project[];
  currentProject: Project | null;
  tables: Table[];
  currentTable: Table | null;
  isTablesLoading: boolean;
  permissionTier: PermissionTier;
  role: string;
  setToken: (token: string) => void;
  setUser: (user: User | null) => void;
  setCurrentOrg: (org: Organization) => void;
  setCurrentProject: (proj: Project | null) => void;
  setCurrentTable: (table: Table | null) => void;
  refreshOrganizations: () => Promise<void>;
  createOrganization: (name: string) => Promise<{ organization: Organization; project: Project }>;
  createProject: (orgId: string, name: string) => Promise<Project>;
  switchOrganization: (org: Organization) => Promise<void>;
  refreshProjects: () => Promise<void>;
  refreshTables: (targetProjectId?: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (data: { email: string; password: string; full_name: string; organization_name: string }) => Promise<void>;
  setAuthSession: (token: string, user: User, org?: Organization, project?: Project, role?: string, permissionTier?: PermissionTier) => void;
  updateProfile: (data: { full_name: string; avatar_url?: string }) => Promise<void>;
  changePassword: (data: { current_password: string; new_password: string }) => Promise<void>;
  logout: () => void;
  canRead: boolean;
  canEdit: boolean;
  canAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setTokenState] = useState<string | null>(() => {
    return localStorage.getItem('ericius_auth_token') || null;
  });

  const [user, setUserState] = useState<User | null>(() => {
    const saved = localStorage.getItem('ericius_user');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // ignore JSON parse error
      }
    }
    return null;
  });

  const [currentOrg, setCurrentOrgState] = useState<Organization | null>(() => {
    const saved = localStorage.getItem('ericius_active_org_data');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {}
    }
    return null;
  });

  const [organizations, setOrganizations] = useState<UserOrganizationSummary[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProjectState] = useState<Project | null>(() => {
    const saved = localStorage.getItem('ericius_active_project_data');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {}
    }
    return null;
  });
  const [tables, setTables] = useState<Table[]>([]);
  const [currentTable, setCurrentTableState] = useState<Table | null>(null);
  const [isTablesLoading, setIsTablesLoading] = useState(false);
  const lastFetchedOrgIdRef = useRef<string | null>(null);
  const lastFetchedProjIdRef = useRef<string | null>(null);

  const [permissionTier, setPermissionTier] = useState<PermissionTier>(() => {
    return (localStorage.getItem('ericius_permission_tier') as PermissionTier) || 'full';
  });

  const [role, setRole] = useState<string>(() => {
    return localStorage.getItem('ericius_role') || 'owner';
  });

  // Set token and synchronize with storage
  const setToken = useCallback((newToken: string) => {
    localStorage.setItem('ericius_auth_token', newToken);
    setTokenState(newToken);
  }, []);

  const setUser = useCallback((newUser: User | null) => {
    setUserState(newUser);
    if (newUser) {
      localStorage.setItem('ericius_user', JSON.stringify(newUser));
    } else {
      localStorage.removeItem('ericius_user');
    }
  }, []);

  const setAuthSession = useCallback(
    (
      newToken: string,
      newUser: User,
      org?: Organization,
      project?: Project,
      sessionRole?: string,
      sessionPermTier?: PermissionTier
    ) => {
      localStorage.setItem('ericius_auth_token', newToken);
      localStorage.setItem('ericius_user', JSON.stringify(newUser));
      setTokenState(newToken);
      setUserState(newUser);

      let effectiveRole = sessionRole || (newUser.is_superadmin ? 'admin' : (org as any)?.role || 'owner');
      if (newUser.is_superadmin) {
        effectiveRole = 'admin';
      }
      setRole(effectiveRole);
      localStorage.setItem('ericius_role', effectiveRole);

      const effectiveTier = (newUser.is_superadmin || effectiveRole === 'owner' || effectiveRole === 'admin')
        ? 'full'
        : (sessionPermTier || 'edit');
      setPermissionTier(effectiveTier);
      localStorage.setItem('ericius_permission_tier', effectiveTier);

      if (org) {
        setCurrentOrgState(org);
        localStorage.setItem('ericius_active_org', org.id);
        localStorage.setItem('ericius_active_org_data', JSON.stringify(org));
      }
      if (project) {
        setCurrentProjectState(project);
        localStorage.setItem('ericius_active_project', project.id);
        localStorage.setItem('ericius_active_project_data', JSON.stringify(project));
        localStorage.removeItem('ericius_active_table');
      }
    },
    []
  );

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await apiClient<{
        token: string;
        user: User;
        organization?: Organization;
        project?: Project;
        role?: string;
        permission_tier?: PermissionTier;
      }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      setAuthSession(res.token, res.user, res.organization, res.project, res.role, res.permission_tier);
    },
    [setAuthSession]
  );

  const register = useCallback(
    async (data: { email: string; password: string; full_name: string; organization_name: string }) => {
      const res = await apiClient<{
        token: string;
        user: User;
        organization?: Organization;
        project?: Project;
        role?: string;
        permission_tier?: PermissionTier;
      }>('/auth/register', {
        method: 'POST',
        body: JSON.stringify(data),
      });

      setAuthSession(res.token, res.user, res.organization, res.project, res.role || 'owner', res.permission_tier || 'full');
    },
    [setAuthSession]
  );

  const updateProfile = useCallback(
    async (data: { full_name: string; avatar_url?: string }) => {
      const res = await apiClient<{ user: User; message: string }>('/auth/profile', {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      setUserState(res.user);
      localStorage.setItem('ericius_user', JSON.stringify(res.user));
    },
    []
  );

  const changePassword = useCallback(
    async (data: { current_password: string; new_password: string }) => {
      await apiClient<{ status: string; message: string }>('/auth/password', {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },
    []
  );

  const logout = useCallback(async () => {
    try {
      if (token && !token.startsWith('token_')) {
        await apiClient('/auth/logout', { method: 'POST' }).catch(() => {});
      }
    } catch {
      // Ignore network failure during logout
    }
    localStorage.removeItem('ericius_auth_token');
    localStorage.removeItem('ericius_user');
    localStorage.removeItem('ericius_active_org');
    localStorage.removeItem('ericius_active_org_data');
    localStorage.removeItem('ericius_active_project');
    localStorage.removeItem('ericius_active_project_data');
    localStorage.removeItem('ericius_active_table');
    localStorage.removeItem('ericius_permission_tier');
    localStorage.removeItem('ericius_role');
    setTokenState(null);
    setUserState(null);
    setCurrentOrgState(null);
    setCurrentProjectState(null);
    setCurrentTableState(null);
    setTables([]);
    setProjects([]);
  }, [token]);

  // Synchronize authenticated session details on mount
  const hasFetchedMeRef = useRef(false);
  useEffect(() => {
    if (!token || hasFetchedMeRef.current) return;
    hasFetchedMeRef.current = true;
    apiClient<{
      user: User;
      organization_id: string;
      organization?: Organization;
      project?: Project;
      role: string;
      permission_tier: PermissionTier;
      is_superadmin: boolean;
    }>('/auth/me')
      .then((res) => {
        if (res.user) {
          setUserState((prev) => (JSON.stringify(prev) === JSON.stringify(res.user) ? prev : res.user));
          localStorage.setItem('ericius_user', JSON.stringify(res.user));
        }
        if (res.role) {
          setRole((prev) => (prev === res.role ? prev : res.role));
          localStorage.setItem('ericius_role', res.role);
        }
        const effectiveTier = (res.is_superadmin || res.role === 'owner' || res.role === 'admin')
          ? 'full'
          : res.permission_tier;
        if (effectiveTier) {
          setPermissionTier((prev) => (prev === effectiveTier ? prev : effectiveTier));
          localStorage.setItem('ericius_permission_tier', effectiveTier);
        }
        if (res.organization) {
          setCurrentOrgState((prev) => {
            const orgMatch = window.location.pathname.match(/\/organization\/([^/]+)/);
            if (orgMatch && orgMatch[1]) return prev;
            if (prev?.id) return prev;
            localStorage.setItem('ericius_active_org', res.organization!.id);
            localStorage.setItem('ericius_active_org_data', JSON.stringify(res.organization));
            return res.organization!;
          });
        }
        if (res.project) {
          setCurrentProjectState((prev) => {
            const projMatch = window.location.pathname.match(/\/projects\/([^/]+)/);
            if (projMatch && projMatch[1]) return prev;
            if (prev?.id) return prev;
            localStorage.setItem('ericius_active_project', res.project!.id);
            localStorage.setItem('ericius_active_project_data', JSON.stringify(res.project));
            return res.project!;
          });
        }
      })
      .catch((err) => {
        if (err?.status === 401) {
          logout();
        }
      });
  }, [token, logout]);

  // Fetch projects for the active organization
  const refreshProjects = useCallback(async () => {
    if (!currentOrg?.id) {
      setProjects([]);
      setCurrentProjectState(null);
      return;
    }
    const orgId = currentOrg.id;
    try {
      const res = await apiClient<{ data: Project[]; total: number }>(
        `/organizations/${orgId}/projects`
      );
      const list = res.data || [];
      setProjects(list);

      const isOrgOnlyRoute =
        typeof window !== 'undefined' &&
        window.location.pathname.startsWith('/organization/') &&
        !window.location.pathname.includes('/projects/');

      if (isOrgOnlyRoute) {
        setCurrentProjectState(null);
        localStorage.removeItem('ericius_active_project');
        localStorage.removeItem('ericius_active_project_data');
        return;
      }

      const savedProjId = localStorage.getItem('ericius_active_project');
      const matched = savedProjId ? list.find((p) => p.id === savedProjId) || null : null;
      setCurrentProjectState((prev) => {
        if (prev && list.some((p) => p.id === prev.id)) {
          return prev;
        }
        if (matched) return matched;
        return list[0] || null;
      });

      if (matched) {
        localStorage.setItem('ericius_active_project', matched.id);
        localStorage.setItem('ericius_active_project_data', JSON.stringify(matched));
      } else if (list.length > 0) {
        localStorage.setItem('ericius_active_project', list[0].id);
        localStorage.setItem('ericius_active_project_data', JSON.stringify(list[0]));
      } else {
        localStorage.removeItem('ericius_active_project');
        localStorage.removeItem('ericius_active_project_data');
      }
    } catch (err) {
      console.warn('Could not load projects from API:', err);
      setProjects([]);
      setCurrentProjectState(null);
    }
  }, [currentOrg?.id]);

  // Fetch tables for current project with active table synchronization
  const refreshTables = useCallback(
    async (targetProjectId?: string) => {
      const projId = targetProjectId || currentProject?.id;
      if (!projId) {
        setTables([]);
        setCurrentTableState(null);
        setIsTablesLoading(false);
        return;
      }
      setIsTablesLoading(true);
      try {
        const res = await apiClient<{ data: Table[]; total: number }>(
          `/projects/${projId}/tables`
        );
        const list = res.data || [];
        setTables(list);

        const savedTableId = localStorage.getItem('ericius_active_table');
        // Synchronize and ensure savedTableId belongs to the current project
        const matched = list.find((t) => t.id === savedTableId);
        const chosen = matched || list[0] || null;
        setCurrentTableState((prev) => {
          if (prev && list.some((t) => t.id === prev.id)) {
            return prev;
          }
          return chosen;
        });
        if (chosen) {
          localStorage.setItem('ericius_active_table', chosen.id);
        } else {
          localStorage.removeItem('ericius_active_table');
        }
      } catch (err) {
        console.warn('Could not load tables from API:', err);
        lastFetchedProjIdRef.current = null;
        setTables([]);
        setCurrentTableState(null);
      } finally {
        setIsTablesLoading(false);
      }
    },
    [currentProject?.id]
  );

  // Fetch organizations the current user belongs to
  const refreshOrganizations = useCallback(async () => {
    if (!token) {
      setOrganizations([]);
      return;
    }
    try {
      const res = await apiClient<{ data: UserOrganizationSummary[]; total: number }>(
        '/user/organizations'
      );
      const list = res.data || [];
      setOrganizations(list);

      // If currentOrg is not set, or no longer in list, default to first
      if (list.length > 0) {
        const savedOrgId = localStorage.getItem('ericius_active_org');
        const matched = list.find((o) => o.id === savedOrgId) || list[0];
        if (matched.role) {
          setRole(matched.role);
          localStorage.setItem('ericius_role', matched.role);
          const effectiveTier = (user?.is_superadmin || matched.role === 'owner' || matched.role === 'admin')
            ? 'full'
            : 'edit';
          setPermissionTier(effectiveTier);
          localStorage.setItem('ericius_permission_tier', effectiveTier);
        }
        setCurrentOrgState((prev) => {
          if (prev && prev.id === matched.id) return prev;
          localStorage.setItem('ericius_active_org', matched.id);
          localStorage.setItem('ericius_active_org_data', JSON.stringify(matched));
          return matched;
        });
      }
    } catch (err) {
      console.warn('Could not load organizations from API:', err);
    }
  }, [token, user?.is_superadmin]);

  useEffect(() => {
    refreshOrganizations();
  }, [token, refreshOrganizations]);

  // Switch active organization cleanly without cross-tenant project leakage
  const switchOrganization = useCallback(
    async (org: Organization) => {
      setCurrentOrgState(org);
      localStorage.setItem('ericius_active_org', org.id);
      localStorage.setItem('ericius_active_org_data', JSON.stringify(org));

      const orgRole = (org as any).role || (user?.is_superadmin ? 'admin' : 'member');
      setRole(orgRole);
      localStorage.setItem('ericius_role', orgRole);
      const effectiveTier = (user?.is_superadmin || orgRole === 'owner' || orgRole === 'admin')
        ? 'full'
        : 'edit';
      setPermissionTier(effectiveTier);
      localStorage.setItem('ericius_permission_tier', effectiveTier);

      // Reset project and table state while loading new org's projects
      localStorage.removeItem('ericius_active_project');
      localStorage.removeItem('ericius_active_project_data');
      localStorage.removeItem('ericius_active_table');
      setCurrentProjectState(null);
      setCurrentTableState(null);
      setTables([]);
      lastFetchedProjIdRef.current = null;

      try {
        const res = await apiClient<{ data: Project[]; total: number }>(
          `/organizations/${org.id}/projects`
        );
        const list = res.data || [];
        setProjects(list);
        if (list.length > 0) {
          const projMatch = window.location.pathname.match(/\/projects\/([^/]+)/);
          const targetProj = projMatch ? list.find((p) => p.id === projMatch[1]) : null;
          const chosenProj = targetProj || list[0];
          setCurrentProjectState(chosenProj);
          lastFetchedProjIdRef.current = chosenProj.id;
          localStorage.setItem('ericius_active_project', chosenProj.id);
          localStorage.setItem('ericius_active_project_data', JSON.stringify(chosenProj));
          refreshTables(chosenProj.id);
        }
      } catch (err) {
        console.warn('Failed to switch organization projects:', err);
        setProjects([]);
      }
    },
    [refreshTables]
  );

  // Create a new organization and seamlessly switch into it
  const createOrganization = useCallback(
    async (name: string) => {
      const res = await apiClient<{
        organization: Organization;
        project: Project;
        message: string;
      }>('/organizations', {
        method: 'POST',
        body: JSON.stringify({ name }),
      });
      await refreshOrganizations();
      if (res.organization) {
        await switchOrganization(res.organization);
      }
      return res;
    },
    [refreshOrganizations, switchOrganization]
  );

  // Create a project under the active organization
  const createProject = useCallback(
    async (orgId: string, name: string) => {
      const project = await apiClient<Project>(`/organizations/${orgId}/projects`, {
        method: 'POST',
        body: JSON.stringify({ name }),
      });
      await refreshProjects();
      setCurrentProject(project);
      return project;
    },
    [refreshProjects]
  );

  useEffect(() => {
    if (!currentOrg?.id) {
      lastFetchedOrgIdRef.current = null;
      return;
    }
    if (lastFetchedOrgIdRef.current === currentOrg.id) return;
    lastFetchedOrgIdRef.current = currentOrg.id;
    refreshProjects();
  }, [currentOrg?.id, refreshProjects]);

  useEffect(() => {
    if (!currentProject?.id) {
      lastFetchedProjIdRef.current = null;
      return;
    }
    if (lastFetchedProjIdRef.current === currentProject.id) return;
    lastFetchedProjIdRef.current = currentProject.id;
    refreshTables(currentProject.id);
  }, [currentProject?.id, refreshTables]);

  const setCurrentOrg = (org: Organization) => {
    switchOrganization(org);
  };

  const setCurrentProject = useCallback(
    (proj: Project | null) => {
      if (!proj) {
        setCurrentProjectState(null);
        lastFetchedProjIdRef.current = null;
        localStorage.removeItem('ericius_active_project');
        localStorage.removeItem('ericius_active_project_data');
        localStorage.removeItem('ericius_active_table');
        setCurrentTableState(null);
        setTables([]);
        return;
      }
      if (currentProject?.id === proj.id && tables.length > 0) {
        // Idempotent: Never clear or wipe tables when selecting the already active project with loaded tables
        return;
      }
      setCurrentProjectState(proj);
      lastFetchedProjIdRef.current = proj.id;
      localStorage.setItem('ericius_active_project', proj.id);
      localStorage.setItem('ericius_active_project_data', JSON.stringify(proj));
      localStorage.removeItem('ericius_active_table');
      setCurrentTableState(null);
      refreshTables(proj.id);
    },
    [currentProject?.id, tables.length, refreshTables]
  );

  const setCurrentTable = (table: Table | null) => {
    if (currentTable?.id === table?.id) return;
    setCurrentTableState(table);
    if (table) {
      localStorage.setItem('ericius_active_table', table.id);
    } else {
      localStorage.removeItem('ericius_active_table');
    }
  };

  const value: AuthContextType = {
    token,
    user,
    isAuthenticated: !!token,
    organizations,
    currentOrg,
    projects,
    currentProject,
    tables,
    currentTable,
    isTablesLoading,
    permissionTier,
    role,
    setToken,
    setUser,
    setCurrentOrg,
    setCurrentProject,
    setCurrentTable,
    refreshOrganizations,
    createOrganization,
    createProject,
    switchOrganization,
    refreshProjects,
    refreshTables,
    login,
    register,
    setAuthSession,
    updateProfile,
    changePassword,
    logout,
    canRead: permissionTier === 'read' || permissionTier === 'edit' || permissionTier === 'full',
    canEdit: permissionTier === 'edit' || permissionTier === 'full',
    canAdmin: permissionTier === 'full',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
